// Anniversary category → lucide icon mapping (W3.1 §3.1 + §5.3).
//
// Single source of truth so TimelineEventCard, CategoryPicker, and the
// detail hero all render the same icon for the same category. No emoji
// (per AGENTS.md §3 #19 + Apple HIG icon library rule).
//
// Categories: birthday / festival / anniversary / date / travel / custom / personal
//
// `personal` is BR-15 reserved for system-generated member birthdays. W36.1
// (profile spec §3.3) corrected the note that used to sit here, which said
// `is_auto_generated=true` rows "aren't user-editable" — they are not fully
// editable, but they are not inert either: `reminder_days` is open to **both**
// members, because a birthday reminder is shared even though the birthday
// itself belongs to the person who was born on it (profile spec §2.1 P1).
// Everything else on such a row is derived from `User.birthday` /
// `display_name` and is rejected with 422 by
// `validate_auto_generated_patch`. The category still barely reaches this file:
// the UI shows it on a system-created row the user did not make.

import {
  Cake,
  Gift,
  Heart,
  Plane,
  Sparkles,
  Sun,
  type LucideIcon,
} from "lucide-react";

export const ANNIVERSARY_CATEGORIES: readonly string[] = Object.freeze([
  "birthday",
  "festival",
  "anniversary",
  "date",
  "travel",
  "custom",
  "personal",
]);

export type AnniversaryCategory =
  | "birthday"
  | "festival"
  | "anniversary"
  | "date"
  | "travel"
  | "custom"
  | "personal";

/** Whitelist guard: keeps `categoryIcon` honest when called from JSON. */
export function isAnniversaryCategory(value: unknown): value is AnniversaryCategory {
  return (
    typeof value === "string" &&
    (ANNIVERSARY_CATEGORIES as readonly string[]).includes(value)
  );
}

const CATEGORY_TO_ICON: Readonly<Record<AnniversaryCategory, LucideIcon>> = Object.freeze({
  birthday: Cake,
  festival: Sun,
  anniversary: Heart,
  date: Sparkles,
  travel: Plane,
  custom: Sparkles,
  personal: Gift,
});

/**
 * Get the lucide icon component for an anniversary category.
 * Falls back to `Sparkles` for malformed values so the UI doesn't crash.
 */
export function categoryIcon(category: string | null | undefined): LucideIcon {
  if (isAnniversaryCategory(category)) return CATEGORY_TO_ICON[category];
  return Sparkles;
}

/** Same as `categoryIcon` but as a plain string (handy for test fixtures). */
export function categoryIconName(category: string | null | undefined): string {
  return categoryIcon(category).displayName ?? categoryIcon(category).name ?? "Sparkles";
}

/**
 * 繁體中文 category labels (per AGENTS.md §3 #14 — Mandarin-flavored, never
 * Cantonese). Used by CategoryPicker chip text + EventCard aria-label.
 */
export const CATEGORY_LABEL_ZH: Readonly<Record<AnniversaryCategory, string>> = Object.freeze({
  birthday: "生日",
  festival: "節日",
  anniversary: "紀念",
  date: "約會",
  travel: "旅行",
  custom: "其他",
  personal: "成員生日",
});

export function categoryLabelZh(category: string | null | undefined): string {
  if (isAnniversaryCategory(category)) return CATEGORY_LABEL_ZH[category];
  return "其他";
}